import { firstValueFrom } from "rxjs";

// This import has been flagged as unallowed for this class. It may be involved in a circular dependency loop.
// eslint-disable-next-line no-restricted-imports
import { PasswordInputResult } from "@bitwarden/auth/angular";
import { PolicyService } from "@bitwarden/common/admin-console/abstractions/policy/policy.service.abstraction";
import { MasterPasswordPolicyOptions } from "@bitwarden/common/admin-console/models/domain/master-password-policy-options";
import { Account } from "@bitwarden/common/auth/abstractions/account.service";
import { MasterPasswordApiService } from "@bitwarden/common/auth/abstractions/master-password-api.service.abstraction";
import { PasswordRequest } from "@bitwarden/common/auth/models/request/password.request";
import { UpdateTempPasswordRequest } from "@bitwarden/common/auth/models/request/update-temp-password.request";
import { OrganizationInviteService } from "@bitwarden/common/auth/organization-invite";
import { assertNonNullish, assertTruthy } from "@bitwarden/common/auth/utils";
import { MasterPasswordUnlockService } from "@bitwarden/common/key-management/master-password/abstractions/master-password-unlock.service";
import { InternalMasterPasswordServiceAbstraction } from "@bitwarden/common/key-management/master-password/abstractions/master-password.service.abstraction";
import {
  MasterPasswordAuthenticationData,
  MasterPasswordSalt,
  MasterPasswordUnlockData,
} from "@bitwarden/common/key-management/master-password/types/master-password.types";
import { firstValueFromOrThrow } from "@bitwarden/common/key-management/utils";
import { UserId } from "@bitwarden/common/types/guid";
import { UserKey } from "@bitwarden/common/types/key";
import { KdfConfig, KeyService } from "@bitwarden/key-management";

import {
  ChangePasswordService,
  InvalidCurrentPasswordError,
} from "./change-password.service.abstraction";

export class DefaultChangePasswordService implements ChangePasswordService {
  constructor(
    protected keyService: KeyService,
    protected masterPasswordApiService: MasterPasswordApiService,
    protected masterPasswordService: InternalMasterPasswordServiceAbstraction,
    protected masterPasswordUnlockService: MasterPasswordUnlockService,
    protected policyService: PolicyService,
    protected organizationInviteService: OrganizationInviteService,
  ) {}

  async changePasswordAndRotateUserKey(
    passwordInputResult: PasswordInputResult,
    user: Account,
  ): Promise<void> {
    throw new Error("changePasswordAndRotateUserKey() is only implemented in Web");
  }

  async changePassword(passwordInputResult: PasswordInputResult, userId: UserId) {
    const context = "Could not change password.";
    assertTruthy(passwordInputResult.currentPassword, "currentPassword", context);
    assertTruthy(passwordInputResult.newPassword, "newPassword", context);
    assertNonNullish(passwordInputResult.kdfConfig, "kdfConfig", context);
    assertTruthy(passwordInputResult.salt, "salt", context);
    assertNonNullish(passwordInputResult.newPasswordHint, "newPasswordHint", context); // can have an empty string as a meaningful value, so check non-nullish

    // Verify that the current password is correct
    const currentPasswordVerified = await this.masterPasswordUnlockService.proofOfDecryption(
      passwordInputResult.currentPassword,
      userId,
    );

    if (!currentPasswordVerified) {
      throw new InvalidCurrentPasswordError();
    }

    // Current password has been verified, so get the user key from state
    const userKey = await firstValueFromOrThrow(this.keyService.userKey$(userId), "userKey");

    // Use current password to make current auth data so we can send the current auth hash to the server
    const currentAuthenticationData =
      await this.masterPasswordService.makeMasterPasswordAuthenticationData(
        passwordInputResult.currentPassword,
        passwordInputResult.kdfConfig,
        passwordInputResult.salt,
      );

    // Use new password to make new auth and unlock data that we can send to the server
    const { newAuthenticationData, newUnlockData } = await this.makeNewAuthAndUnlockData(
      passwordInputResult.newPassword,
      passwordInputResult.kdfConfig,
      passwordInputResult.salt,
      userKey,
    );

    const request = new PasswordRequest(
      currentAuthenticationData.masterPasswordAuthenticationHash,
      newAuthenticationData,
      newUnlockData,
      passwordInputResult.newPasswordHint,
    );

    await this.masterPasswordApiService.postPassword(request);
  }

  async changePasswordForAccountRecovery(passwordInputResult: PasswordInputResult, userId: UserId) {
    const context = "Could not change password for account recovery.";
    assertTruthy(passwordInputResult.currentPassword, "currentPassword", context);
    assertTruthy(passwordInputResult.newPassword, "newPassword", context);
    assertNonNullish(passwordInputResult.kdfConfig, "kdfConfig", context);
    assertTruthy(passwordInputResult.salt, "salt", context);
    assertNonNullish(passwordInputResult.newPasswordHint, "newPasswordHint", context); // can have an empty string as a meaningful value, so check non-nullish

    // Verify that the current password is correct
    const currentPasswordVerified = await this.masterPasswordUnlockService.proofOfDecryption(
      passwordInputResult.currentPassword,
      userId,
    );

    if (!currentPasswordVerified) {
      throw new InvalidCurrentPasswordError();
    }

    // Current password has been verified, so get the user key from state
    const userKey = await firstValueFromOrThrow(this.keyService.userKey$(userId), "userKey");

    // Use new password to make new auth and unlock data that we can send to the server
    const { newAuthenticationData, newUnlockData } = await this.makeNewAuthAndUnlockData(
      passwordInputResult.newPassword,
      passwordInputResult.kdfConfig,
      passwordInputResult.salt,
      userKey,
    );

    const request = UpdateTempPasswordRequest.newConstructorWithHint(
      newAuthenticationData,
      newUnlockData,
      passwordInputResult.newPasswordHint,
    );

    // TODO: PM-23047 will look to consolidate this into the change password endpoint.
    await this.masterPasswordApiService.putUpdateTempPassword(request);
  }

  private async makeNewAuthAndUnlockData(
    newPassword: string,
    kdfConfig: KdfConfig,
    salt: MasterPasswordSalt,
    userKey: UserKey,
  ): Promise<{
    newAuthenticationData: MasterPasswordAuthenticationData;
    newUnlockData: MasterPasswordUnlockData;
  }> {
    const newAuthenticationData =
      await this.masterPasswordService.makeMasterPasswordAuthenticationData(
        newPassword,
        kdfConfig,
        salt,
      );

    const newUnlockData = await this.masterPasswordService.makeMasterPasswordUnlockData(
      newPassword,
      kdfConfig,
      salt,
      userKey,
    );

    return { newAuthenticationData, newUnlockData };
  }

  /**
   * Don't navigate for most clients.
   *
   * For example, on web, routing to root would break org invite email acceptance flows for the change password
   * flow where a user is subject to MP policy requirements (i.e. has ForceSetPasswordReason.WeakMasterPassword).
   * This is because routing to root would call the redirectGuard, which routes to /vault (as the user is still
   * unlocked), which would clear the deepLinkRedirectUrl prematurely via deepLinkGuard. [Note: LogoutService
   * behavior and routing will be investigated in https://bitwarden.atlassian.net/browse/PM-32660]
   *
   * @returns false
   */
  shouldNavigateToRoot(): boolean {
    return false;
  }

  async resolveMasterPasswordPolicyOptions(
    userId: UserId,
  ): Promise<MasterPasswordPolicyOptions | undefined> {
    // Get existing MP policy options from state for any orgs the user is in (accepted / confirmed)
    const statePasswordPolicyOptions = await firstValueFrom(
      this.policyService.masterPasswordPolicyOptions$(userId),
    );

    const orgInvite = await this.organizationInviteService.getOrganizationInvite();
    const invitePasswordPolicyOptions = orgInvite
      ? await this.organizationInviteService.getMasterPasswordPolicyOptionsForInvite(orgInvite)
      : undefined;

    // Short-circuit when one source has no MP policy so the strictness merge gets two real inputs.
    if (statePasswordPolicyOptions == null) {
      return invitePasswordPolicyOptions;
    }
    if (invitePasswordPolicyOptions == null) {
      return statePasswordPolicyOptions;
    }
    // Both sources have MP requirements - merge into the strictest set across joined orgs + invite.
    return this.policyService.combineMasterPasswordPolicyOptions(
      statePasswordPolicyOptions,
      invitePasswordPolicyOptions,
    );
  }
}
